import React, { useState, useEffect } from 'react';
import { 
  verifyPasswordResetCode, 
  confirmPasswordReset, 
  applyActionCode,
  checkActionCode
} from 'firebase/auth';
import { auth } from '../../services/firebase';
import { Lock, CheckCircle, AlertCircle, Trophy, Mail, RefreshCw } from 'lucide-react';

const AuthAction = () => {
  const [mode, setMode] = useState('');
  const [oobCode, setOobCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');
  
  // Password reset specific states
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validCode, setValidCode] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Helper function to get correct base URL
  const getBaseUrl = () => {
    // Use the current origin to maintain the correct domain
    return window.location.origin;
  };

  useEffect(() => {
    // Get parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const modeParam = urlParams.get('mode');
    const codeParam = urlParams.get('oobCode');
    const apiKey = urlParams.get('apiKey');
    const continueUrl = urlParams.get('continueUrl');
    
    console.log('🔵 Auth Action - Processing:', {
      mode: modeParam,
      hasCode: !!codeParam,
      apiKey: apiKey ? 'Present' : 'Missing',
      continueUrl,
      fullUrl: window.location.href,
      origin: window.location.origin
    });

    if (urlParams.get('verified') === 'true') {
      console.log('✅ User redirected from Firebase Auth with verified=true');
      setMode('verifyEmail');
      setSuccess(true);
      setLoading(false);
      
      // Redirect after 2 seconds
      setTimeout(() => {
        window.location.href = `${getBaseUrl()}/dashboard`;
      }, 2000);
      return;
    }

    if (!codeParam) {
      console.error('❌ Missing action code in URL');
      setError('Invalid or missing action code. The link may be corrupted or incomplete.');
      setLoading(false);
      return;
    }

    if (!modeParam) {
      console.error('❌ Missing mode in URL');
      setError('Invalid action link. Please request a new one.');
      setLoading(false);
      return;
    }

    setOobCode(codeParam);
    setMode(modeParam);

    // Handle different modes
    switch (modeParam) {
      case 'resetPassword':
        verifyResetCode(codeParam);
        break;
      case 'verifyEmail':
      case 'verifyAndChangeEmail':
        verifyEmail(codeParam);
        break;
      case 'recoverEmail':
        recoverEmail(codeParam);
        break;
      default:
        setError('Invalid action type');
        setLoading(false);
    }
  }, []);

  // ============ PASSWORD RESET ============
  const verifyResetCode = async (code) => {
    try {
      const userEmail = await verifyPasswordResetCode(auth, code);
      setEmail(userEmail);
      setValidCode(true);
      setLoading(false);
    } catch (error) {
      console.error('Error verifying reset code:', error);
      handleAuthError(error);
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');

    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setResetting(true);
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      console.log('✅ Password reset successful');
      setSuccess(true);
      
      setTimeout(() => {
        window.location.href = `${getBaseUrl()}/`;
      }, 3000);
    } catch (error) {
      console.error('Error resetting password:', error);
      handleAuthError(error);
    } finally {
      setResetting(false);
    }
  };

  // ============ EMAIL VERIFICATION ============
  const verifyEmail = async (code) => {
    try {
      // First check if the code is valid
      const info = await checkActionCode(auth, code);
      console.log('✅ Action code valid for:', info.data.email);
      
      // Apply the verification
      await applyActionCode(auth, code);
      console.log('✅ Email verification successful');
      
      // If user is currently logged in, reload to update emailVerified status
      if (auth.currentUser) {
        await auth.currentUser.reload();
        console.log('✅ User reloaded, emailVerified:', auth.currentUser.emailVerified);
      }
      
      setEmail(info.data.email);
      setSuccess(true);
      setLoading(false);
      
      // Redirect after 2 seconds - to dashboard where they'll see registration form if needed
      setTimeout(() => {
        window.location.href = `${getBaseUrl()}/dashboard`;
      }, 2000);
    } catch (error) {
      console.error('Error verifying email:', error);
      
      // Special handling: if code is invalid but user is already verified
      if (error.code === 'auth/invalid-action-code' && auth.currentUser?.emailVerified) {
        console.log('✅ User email already verified');
        setSuccess(true);
        setLoading(false);
        setTimeout(() => {
          window.location.href = `${getBaseUrl()}/dashboard`;
        }, 2000);
        return;
      }
      
      // For invalid/expired codes when user isn't logged in (different browser scenario)
      if ((error.code === 'auth/invalid-action-code' || error.code === 'auth/expired-action-code') && !auth.currentUser) {
        setError('CROSS_BROWSER_VERIFICATION');
        setLoading(false);
        return;
      }
      
      handleAuthError(error);
      setLoading(false);
    }
  };

  // ============ EMAIL RECOVERY ============
  const recoverEmail = async (code) => {
    try {
      const info = await checkActionCode(auth, code);
      await applyActionCode(auth, code);
      setEmail(info.data.email);
      console.log('✅ Email recovery successful');
      setSuccess(true);
      setLoading(false);
      
      setTimeout(() => {
        window.location.href = `${getBaseUrl()}/`;
      }, 3000);
    } catch (error) {
      console.error('Error recovering email:', error);
      handleAuthError(error);
      setLoading(false);
    }
  };

  // ============ ERROR HANDLING ============
  const handleAuthError = (error) => {
    switch (error.code) {
      case 'auth/invalid-action-code':
        setError('This link is invalid or has already been used. Please request a new one.');
        break;
      case 'auth/expired-action-code':
        setError('This link has expired. Please request a new one.');
        break;
      case 'auth/user-disabled':
        setError('This account has been disabled. Please contact support.');
        break;
      case 'auth/user-not-found':
        setError('No account found for this email address.');
        break;
      case 'auth/weak-password':
        setError('Password is too weak. Please use a stronger password.');
        break;
      default:
        setError('An error occurred. Please try again or request a new link.');
    }
  };

  // ============ RENDER HELPERS ============
  const getTitle = () => {
    switch (mode) {
      case 'resetPassword':
        return 'Reset Password';
      case 'verifyEmail':
        return 'Verify Email';
      case 'recoverEmail':
        return 'Recover Email';
      default:
        return 'Account Action';
    }
  };

  const getSuccessMessage = () => {
    switch (mode) {
      case 'resetPassword':
        return 'Your password has been successfully reset.';
      case 'verifyEmail':
        return 'Your email has been verified! Redirecting to complete your registration...';
      case 'recoverEmail':
        return 'Your email has been successfully recovered.';
      default:
        return 'Action completed successfully.';
    }
  };

  const getSuccessIcon = () => {
    switch (mode) {
      case 'verifyEmail':
        return <Mail className="text-green-600" size={32} />;
      default:
        return <CheckCircle className="text-green-600" size={32} />;
    }
  };

  // ============ LOADING STATE ============
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {mode === 'resetPassword' ? 'Verifying reset link...' : 
             mode === 'verifyEmail' ? 'Verifying your email...' : 
             'Processing...'}
          </p>
        </div>
      </div>
    );
  }

  // ============ SUCCESS STATE ============
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Trophy className="w-12 h-12 text-green-600 mx-auto mb-3" />
            <h1 className="text-4xl font-bold mb-1">
              <span className="text-green-500">Club</span><span className="text-blue-500">Hudl</span>
            </h1>
            <p className="text-xs text-gray-500 font-medium tracking-wide uppercase">Your Club Operations Made Easy</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              {getSuccessIcon()}
            </div>
            <h2 className="text-2xl font-bold mb-2 text-gray-900">Success!</h2>
            <p className="text-gray-600 mb-4">{getSuccessMessage()}</p>
            <p className="text-sm text-gray-500 mb-6">Redirecting to login page...</p>
            <button
              onClick={() => window.location.href = `${getBaseUrl()}/`}
              className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============ ERROR STATE (including initial loading errors) ============
  if (error && !validCode) {
    const isEmailVerificationError = mode === 'verifyEmail';
    const isCrossBrowserError = error === 'CROSS_BROWSER_VERIFICATION';
    const isInitialError = !mode || error.includes('missing action code');
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Trophy className="w-12 h-12 text-green-600 mx-auto mb-3" />
            <h1 className="text-4xl font-bold mb-1">
              <span className="text-green-500">Club</span><span className="text-blue-500">Hudl</span>
            </h1>
            <p className="text-xs text-gray-500 font-medium tracking-wide uppercase">Your Club Operations Made Easy</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-6">
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                isCrossBrowserError ? 'bg-blue-100' : isInitialError ? 'bg-orange-100' : 'bg-red-100'
              }`}>
                {isCrossBrowserError ? (
                  <Mail className="text-blue-600" size={32} />
                ) : isInitialError ? (
                  <AlertCircle className="text-orange-600" size={32} />
                ) : (
                  <AlertCircle className="text-red-600" size={32} />
                )}
              </div>
              <h2 className="text-2xl font-bold mb-2 text-gray-900">
                {isInitialError ? 'Invalid Link' : isCrossBrowserError ? 'Verification Link Used' : isEmailVerificationError ? 'Verification Link Expired' : 'Action Failed'}
              </h2>
            </div>
            
            {isInitialError ? (
              <>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <p className="text-orange-800 text-sm font-medium mb-2">
                    This link appears to be invalid or incomplete.
                  </p>
                  <p className="text-orange-700 text-xs">
                    {error}
                  </p>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-blue-800 text-sm">
                    <strong>Common causes:</strong>
                  </p>
                  <ul className="text-blue-700 text-xs mt-2 space-y-1 list-disc list-inside">
                    <li>The link was split across multiple lines in your email</li>
                    <li>The link was truncated by your email client</li>
                    <li>You're using an old or expired link</li>
                  </ul>
                </div>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <p className="text-green-800 text-sm">
                    <strong>What to do:</strong> Try copying the entire link from your email again, or request a new verification email after logging in.
                  </p>
                </div>
              </>
            ) : isCrossBrowserError ? (
              <>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-blue-800 text-sm">
                    This verification link has already been used or opened in a different browser.
                  </p>
                </div>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <p className="text-green-800 text-sm">
                    <strong>✅ Good news!</strong> If you already verified your email in another browser, just log in below and you're all set!
                  </p>
                </div>
                
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                  <p className="text-orange-800 text-sm">
                    <strong>Haven't verified yet?</strong> Log in with your email and password, then click "Resend Verification Email" to get a new link.
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>

                {isEmailVerificationError && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <p className="text-blue-800 text-sm">
                      <strong>Don't worry!</strong> Just log in and click "Resend Verification Email" to get a new link.
                    </p>
                  </div>
                )}
              </>
            )}

            <button
              onClick={() => window.location.href = `${getBaseUrl()}/login`}
              className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============ PASSWORD RESET FORM ============
  if (mode === 'resetPassword' && validCode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Trophy className="w-12 h-12 text-green-600 mx-auto mb-3" />
            <h1 className="text-4xl font-bold mb-1">
              <span className="text-green-500">Club</span><span className="text-blue-500">Hudl</span>
            </h1>
            <p className="text-xs text-gray-500 font-medium tracking-wide uppercase">Your Club Operations Made Easy</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold mb-2">Reset Password</h2>
              <p className="text-sm text-gray-600">
                Enter a new password for <strong className="text-gray-900">{email}</strong>
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handlePasswordReset} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Enter new password"
                    disabled={resetting}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Confirm New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      confirmPassword && newPassword !== confirmPassword ? 'border-red-300 bg-red-50' : ''
                    }`}
                    placeholder="Confirm new password"
                    disabled={resetting}
                  />
                </div>
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
                )}
                {confirmPassword && newPassword === confirmPassword && confirmPassword.length >= 6 && (
                  <p className="text-xs text-green-600 mt-1">✓ Passwords match</p>
                )}
              </div>

              <button
                type="submit"
                disabled={resetting || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {resetting ? (
                  <>
                    <RefreshCw className="animate-spin" size={18} />
                    Resetting Password...
                  </>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => window.location.href = `${getBaseUrl()}/`}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                ← Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============ ERROR STATE FOR PASSWORD RESET ============
  if (mode === 'resetPassword' && error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Trophy className="w-12 h-12 text-green-600 mx-auto mb-3" />
            <h1 className="text-4xl font-bold mb-1">
              <span className="text-green-500">Club</span><span className="text-blue-500">Hudl</span>
            </h1>
            <p className="text-xs text-gray-500 font-medium tracking-wide uppercase">Your Club Operations Made Easy</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <AlertCircle className="text-red-600" size={32} />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-gray-900">Invalid Reset Link</h2>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800 text-sm">{error}</p>
            </div>

            <button
              onClick={() => window.location.href = `${getBaseUrl()}/`}
              className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Fallback
  return null;
};

export default AuthAction;